import { PortInPoolItem } from '@smarty-nx/data'
import dayjs from 'dayjs'

import { pacRegex, stacRegex } from './regex'
import { DATE_FORMATS } from './dateFormatters'

export const MINIMUM_AGE = 16
export const MAXIMUM_AGE = 115
export const GROUP_MINIMUM_AGE = 18

const REQUIRED_MESSAGE = 'Please enter a date of birth'
const VALID_DATE_MESSAGE = 'Please enter a valid date'
const INSUFFICIENT_MESSAGE = 'You must be at least 16 years old to use SMARTY.'
const GROUP_INSUFFICIENT_MESSAGE =
  'You need to be over 18 years old to start a group'
const INCORRECT_AGE_MESSAGE =
  'Your date of birth looks incorrect, please double check.'

/**
 * Generic form values type for validators that need access to all form fields
 * Redux-form passes all form values as the second parameter to validation functions
 * Uses 'any' to allow flexible form structure with dynamic field keys
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface FormValues extends Record<string, any> {
  email?: string
  email_confirmation?: string
  dob?: string
  first_name?: string
  last_name?: string
  password?: string
  street_1?: string
  street_2?: string
  town?: string
  postal_code?: string
  country?: string
  company?: string

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

export const validatePacOrStac = (value: string) =>
  value && !(pacRegex.test(value) || stacRegex.test(value))
    ? 'Please enter a valid PAC/STAC code'
    : undefined

export const isFullUKNUmber = (num: string) =>
  num &&
  ((num.startsWith('+44') && num.length >= 13) ||
    (num.startsWith('44') && num.length >= 12) ||
    (num.startsWith('07') && num.length >= 11))

export const validateFullUKNUmber = (num: string) =>
  !isFullUKNUmber(num) ? 'Please enter a valid UK phone number' : undefined

export const validateShortPhoneNumber = (num: string) =>
  num &&
  ((num.startsWith('7') && num.length === 10) ||
    (num.startsWith('07') && num.length === 11))
    ? undefined
    : 'Number invalid'

// - If it starts with a '0', it can be 10 digits long.
// - If it doesn't start with a '0', it has to be 9 digits long.
export const validatePhoneNumber = (value: string) =>
  value && !/^(0[0-9]{10}|[1-9][0-9]{9})$/g.test(value)
    ? 'Please enter a valid UK phone number.'
    : undefined

export const validateStacCode = (value: string) =>
  value && !stacRegex.test(value) ? 'Please enter a valid STAC code' : undefined

export const validatePacCode = (value: string) =>
  value && !pacRegex.test(value) ? 'Please enter a valid PAC code' : undefined

export const isUserAboveAge = (dob: string, threshold: number): boolean => {
  const age = dayjs().diff(dayjs(dob, DATE_FORMATS.FULL_DATE), 'years', false)
  return age >= threshold
}

export const isUserAboveEighteen = (dob: string): boolean => {
  return isUserAboveAge(dob, GROUP_MINIMUM_AGE)
}

export const isDuplicate = (
  key: 'msisdn' | 'code',
  value: string,
  addedDetails: PortInPoolItem[]
) => {
  if (addedDetails.some((detail: PortInPoolItem) => detail[key] === value)) {
    return 'Duplicate value provided'
  }
  return undefined
}

export const required = (value?: string): string | undefined => {
  return value?.trim() ? undefined : 'This field is required'
}

export const requiredNoTrim = (value?: string): string | undefined => {
  return value ? undefined : 'This field is required'
}

export const isRequired = (value: string) =>
  value ? undefined : 'Please select'

export const isRequiredDate = (value?: string): string | undefined =>
  !value ? REQUIRED_MESSAGE : undefined

const maxLength =
  (max: number) =>
  (value: string): string | undefined =>
    value && value.length > max
      ? `Must be ${max} characters or less`
      : undefined

const minLength =
  (min: number) =>
  (value: string): string | undefined =>
    value && value.length < min
      ? `Must be ${min} characters or more.`
      : undefined

export const minLength8 = minLength(8)
export const maxLength30 = maxLength(30)
export const maxLength50 = maxLength(50)
export const maxLength100 = maxLength(100)
export const maxLength320 = maxLength(320)

export const minLength12 = minLength(12)

export const maxLength72 = (value: string): string | undefined =>
  value && value.length > 72 ? 'Must be less than 72 characters.' : undefined

export const passwordHint = [
  'Min 12 characters',
  'Tip: recommended to be longer than 15 characters',
]

export const emailPattern =
  /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*)@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]+)\])$/i

export const validateEmail = (value: string) =>
  value && !emailPattern.test(value)
    ? 'Please enter a valid email address'
    : undefined

export const isEmailsMatch = (value: string, allValues: FormValues) =>
  value !== allValues.email ? 'Your emails do not match' : undefined

export const isValidDateFormat = <T>(
  value: string | null,
  _values: T,
  // there are few date inputs, all of them provides date in different formats
  format = DATE_FORMATS.FULL_DATE
): string | undefined => {
  if (value) {
    const date = dayjs(value, format, true)
    return date.isValid() ? undefined : VALID_DATE_MESSAGE
  }

  return undefined
}

export const isMinimumAge =
  (minAge: number, errorMessage: string) =>
  <T>(
    value: string,
    _values: T,
    // there are few date inputs, all of them provides date in different formats
    format = DATE_FORMATS.FULL_DATE
  ): string | undefined => {
    const age = dayjs().diff(dayjs(value, format), 'years', false)
    const ageInsufficient = age < minAge

    if (ageInsufficient) {
      return errorMessage
    }

    return undefined
  }

export const isMinimumAge16 = isMinimumAge(MINIMUM_AGE, INSUFFICIENT_MESSAGE)

export const isMinimumAge18 = isMinimumAge(
  GROUP_MINIMUM_AGE,
  GROUP_INSUFFICIENT_MESSAGE
)

export const isMaximumAge115 = <T>(
  value: string,
  _values: T,
  format = DATE_FORMATS.FULL_DATE
): string | undefined => {
  const age = dayjs().diff(dayjs(value, format), 'years', false)
  return age > MAXIMUM_AGE ? INCORRECT_AGE_MESSAGE : undefined
}

export const isValidJwtToken = (token: string | null): boolean => {
  if (!token) return false

  const jwtRegex = /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]*$/
  return jwtRegex.test(token)
}
